<template>
    <b-card no-body border-variant="light" class="mb-2">
        <b-card-header header-tag="header" class="p-0 border-light custom-header" role="tab">
            <b-row class="mb-2">
                <b-col class="col-auto">
                    <b-button :variant="visible ? 'custom-green' : 'outline-secondary'" class="text-uppercase fs-14 fw-600" @click="displayed = !displayed">
                        {{ title }}
                        <span class="float-right ml-2"><b-icon :icon="displayed ? 'chevron-up' : 'chevron-down'" /> </span>
                    </b-button>
                    <b-button
                        v-if="supplementaryAgreement.status.code === 'signed_by_both' || supplementaryAgreement.status.code === 'signed_by_customer'"
                        :disabled="pdfLoading"
                        @click="downloadPDF"
                        variant="custom-green"
                        class="ml-2 download-pdf"
                        title="Нажмите, чтобы скачать дополнительное соглашение в формате PDF">
                        <b-icon-file-earmark-pdf />
                    </b-button>
                    <signature-xml-info-button
                        v-if="supplementaryAgreement.supplier_signature || supplementaryAgreement.customer_signature"
                        :title="`Электронная подпись к дополнительному соглашению №${supplementaryAgreement.number}`"
                        :supplier-signature="supplementaryAgreement.supplier_signature"
                        :customer-signature="supplementaryAgreement.customer_signature"
                        :allow-download-xml="purchase.flags.is_participant"
                        :filename="`supplementary_agreement_${supplementaryAgreement.number}_signature_info`"
                        class="ml-2"
                        :mt="0" />
                </b-col>
                <b-col class="my-auto">
                    <span class="fs-14 fw-600">{{ supplementaryAgreement.status.title }}</span>
                </b-col>
            </b-row>
        </b-card-header>
        <b-collapse v-model="displayed" :id="id" accordion="supplementary-agreements-accordion" role="tabpanel">
            <b-card-body class="py-0 px-2">
                <text-row :label-cols-lg="4" :value="supplementaryAgreement.termination_type.title" compact label="Основание для расторжения контракта" />
                <text-row-price v-model="supplementaryAgreement.price" :label-cols-lg="4" compact label="Общая стоимость" />
                <text-row-date v-model="supplementaryAgreement.conclusion_date" :label-cols-lg="4" compact label="Дата заключения дополнительного соглашения" />
                <fl44-unknown-volume-supplementary-agreement-files-table :download-zip="downloadZip" :supplementary-agreement="supplementaryAgreement">
                    <template #title> Документы </template>
                </fl44-unknown-volume-supplementary-agreement-files-table>
                <text-row-disagreement-table v-if="supplementaryAgreement.supplementary_agreement_disagreements && supplementaryAgreement.supplementary_agreement_disagreements.length > 0" :disagreements="supplementaryAgreement.supplementary_agreement_disagreements">
                    <template #title>
                        <h5 class="grey-color mt-3 mb-1 main_text font-weight-bold">Разногласия по дополнительному соглашению</h5>
                    </template>
                </text-row-disagreement-table>
                <fl44-unknown-volume-supplementary-agreement-regular-view-specifications-table :deliverables="supplementaryAgreement.deliverables" :total-cost="supplementaryAgreement.price" />
                <template v-if="(supplementaryAgreement.status.code === 'signed_by_both' || supplementaryAgreement.status.code === 'signed_by_customer') && purchase.is_customer && limitsRequired && supplementaryAgreement.limits && supplementaryAgreement.limits.length > 0">
                    <limits-editable-table
                        v-if="isLimitsEdit"
                        v-model="form.limits"
                        :total-cost="supplementaryAgreement.price"
                        @validate="limitsValidate"
                        :limit-year="purchase.purchase_terms.purchase_limit_year"
                        :order-type="purchase.purchase_terms.order_type"
                        :is-data-sending="isDataSending"
                        purchase-type="fl44_unknown_volume"
                        :purchase-id="purchase.id">
                        <template #title>
                            <h5 class="grey-color ml-1 mt-3 mb-1 main_text font-weight-bold">Измените лимиты</h5>
                        </template>
                        <template #label-hint> Добавьте или измените лимиты. Необходимо полностью распределить цену дополнительного соглашения по соответствующим лимитам. </template>
                        <template #label-total> Цена дополнительного соглашения, руб.: </template>
                        <template #label-variance> Отклонение от цены дополнительного соглашения, руб.: </template>
                    </limits-editable-table>
                    <limits-table v-else :items="supplementaryAgreement.limits">
                        <template #title>
                            <h5 class="grey-color ml-1 mb-1 main_text font-weight-bold">Лимиты по КБК</h5>
                        </template>
                    </limits-table>
                    <template v-if="purchase.status.id === 'contract:active' && purchase.contract.latest_supplementary_agreement_id === supplementaryAgreement.id">
                        <template v-if="isLimitsEdit">
                            <b-overlay :show="isDataSending" class="d-inline-block" opacity="0.6" rounded spinner-small spinner-variant="primary">
                                <b-button variant="custom-outline-secondary" class="text-uppercase mt-1 mb-3 mr-3" @click="isLimitsEdit = false">Отмена</b-button>
                            </b-overlay>
                            <b-overlay :show="isDataSending" class="d-inline-block" opacity="0.6" rounded spinner-small spinner-variant="primary">
                                <b-button variant="custom-green" :disabled="!isLimitsValid" class="text-uppercase mt-1 mb-3" @click="updateLimits">Сохранить</b-button>
                            </b-overlay>
                        </template>
                        <template v-else>
                            <b-button variant="custom-green" class="text-uppercase mt-1 mb-3" @click="editLimits">Изменить лимиты</b-button>
                        </template>
                    </template>
                </template>
                <text-row-supplier-accounting v-if="(supplementaryAgreement.status.code === 'signed_by_both' || supplementaryAgreement.status.code === 'signed_by_customer') && purchase.is_customer && purchase.flags.has_contract && supplementaryAgreement.supplier_accounting"
                    v-model="supplementaryAgreement.supplier_accounting">
                    <template #title>
                        <h5 class="grey-color ml-1 mt-3 mb-1 main_text font-weight-bold">Банковские реквизиты поставщика</h5>
                    </template>
                </text-row-supplier-accounting>
                <private-fl44-unknown-volume-supplementary-agreement-supplier-block
                    v-if="purchase.contract.latest_supplementary_agreement_id === supplementaryAgreement.id && $store.getters.isLoggedIn && purchase.is_supplier && purchase.actions.supplier.supplementary_agreement !== 'disallow'"
                    :supplementary-agreement="supplementaryAgreement"
                    :purchase="purchase"
                    @refresh="$emit('refresh')" />
                <private-fl44-unknown-volume-supplementary-agreement-customer-block
                    v-if="purchase.contract.latest_supplementary_agreement_id === supplementaryAgreement.id && $store.getters.isLoggedIn && purchase.is_customer && purchase.actions.customer.supplementary_agreement !== 'disallow'"
                    :purchase="purchase"
                    :supplementary-agreement="supplementaryAgreement"
                    @refresh="$emit('refresh', $event)" />
            </b-card-body>
        </b-collapse>
    </b-card>
</template>

<script>
import cloneDeep from 'lodash.clonedeep';
import Config from '@core/js/ddriven/application/config/Config';
import Fl44UnknownVolumeSupplementaryAgreementFilesTable from '@/components/products/fl44-unknown-volume/partials/fl44-unknown-volume-supplementary-agreement-files-table.vue';
import PrivateFl44UnknownVolumeSupplementaryAgreementCustomerBlock from '@/components/products/fl44-unknown-volume/partials/private-fl44-unknown-volume-supplementary-agreement-customer-block.vue';
import PrivateFl44UnknownVolumeSupplementaryAgreementSupplierBlock from '@/components/products/fl44-unknown-volume/partials/private-fl44-unknown-volume-supplementary-agreement-supplier-block.vue';
import FileDownload from 'js-file-download';
import Fl44UnknownVolumeSupplementaryAgreementRegularViewSpecificationsTable from '@/components/products/fl44-unknown-volume/partials/fl44-unknown-volume-supplementary-agreement-termination-view-specifications-table.vue';

export default {
    name: 'fl44-unknown-volume-supplementary-agreement-termination-item',
    components: { Fl44UnknownVolumeSupplementaryAgreementRegularViewSpecificationsTable, PrivateFl44UnknownVolumeSupplementaryAgreementSupplierBlock, PrivateFl44UnknownVolumeSupplementaryAgreementCustomerBlock, Fl44UnknownVolumeSupplementaryAgreementFilesTable },
    props: {
        supplementaryAgreement: {
            type: Object,
            required: true
        },
        purchase: {
            type: Object,
            required: true
        },
        title: {
            type: String,
            required: true
        },
        visible: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            displayed: this.visible,
            isDataSending: false,
            isLimitsEdit: false,
            isLimitsValid: false,
            pdfLoading: false,
            form: {
                limits: []
            }
        };
    },
    methods: {
        limitsValidate(value) {
            this.isLimitsValid = !value;
        },
        editLimits() {
            this.form.limits = cloneDeep(this.supplementaryAgreement.limits);
            this.isLimitsEdit = true;
        },
        downloadZip() {
            this.$api.documents.downloadWithSignatures(this.supplementaryAgreement.supplementary_agreement_file.supplementary_agreement_attachment.id, this.purchase.contract.zip_name);
        },
        async updateLimits() {
            const form = {};
            form.limits = this.form.limits.map((item) => {
                return {
                    id: item.id,
                    year: item.year,
                    amount_assigned: item.amount_assigned
                };
            });
            this.isDataSending = true;
            this.$api.products.fl44UnknownVolume.supplementaryAgreements
                .updateSupplementaryAgreementLimits(this.purchase.id, this.purchase.contract.id, this.supplementaryAgreement.id, form)
                .then((response) => {
                    if (response) {
                        this.isLimitsEdit = false;
                        this.$emit('refresh');
                    }
                })
                .catch(() => {})
                .finally(() => {
                    this.isDataSending = false;
                });
        },
        downloadPDF() {
            this.pdfLoading = true;
            this.$api.products.fl44UnknownVolume.purchases
                .downloadPurchaseContractSupplementaryAgreementPdf(this.purchase.id, this.supplementaryAgreement.id)
                .then((response) => {
                    const fileName = `Дополнительное соглашение ${this.supplementaryAgreement.type.id === 'termination' ? 'о расторжении ' : ''}№${this.supplementaryAgreement.number} к контракту ${this.purchase.reg_number}.pdf`;
                    FileDownload(response.data, fileName);
                })
                .catch((error) => {
                    this.showError(error);
                })
                .finally(() => {
                    this.pdfLoading = false;
                });
        }
    },
    computed: {
        id() {
            return 'supplementary_agreement_' + this._uid;
        },
        Config() {
            return Config;
        },
        limitsRequired() {
            return this.features.kbk_limits && !this.purchase.purchase_terms.without_limits;
        },
        features() {
            return Config.get('theme.features');
        }
    }
};
</script>

<style scoped>
.custom-header {
    background-color: var(--greyBg-color) !important;
}
.download-pdf {
    width: 35px !important;
    min-width: 35px !important;
    padding-left: 8px !important;
}
</style>
